import React, {useEffect, useMemo, useRef} from 'react';
import {Chart, LogarithmicScale} from 'chart.js/auto';
import {chartColors} from '../../mosaic/charts/ChartjsConfigOrig.jsx';
import {tailwindConfig} from '../../mosaic/utils/Utils';
import {useThemeProvider} from '../../mosaic/utils/ThemeContext.jsx';
import {GraphScale} from './Graph.types.ts';
import {useRenderCurrencyValue} from '../../api/utilities/useValueRenderers.js';
import {getBarColor} from '../../utils/graphColors.js';

export default function BarChart({data, className, style}) {
    const canvasRef = useRef(null);
    const legendRef = useRef(null);
    const chartRef = useRef(null);
    const renderCurrencyValueRef = useRef(null);
    const {currentTheme} = useThemeProvider();
    const darkMode = useMemo(() => currentTheme === 'dark', [currentTheme]);
    const {
        tooltipTitleColor,
        tooltipBodyColor,
        tooltipBgColor,
        tooltipBorderColor
    } = chartColors;
    const renderCurrencyValue = useRenderCurrencyValue();

    useEffect(() => {
        renderCurrencyValueRef.current = renderCurrencyValue;
    }, [renderCurrencyValue]);

    // Prepare datasets, filtering out items with value ≤ 0
    const processedDatasets = useMemo(() => {
        return data.datasets
            .map((ds, i) => {
                const filtered = ds.data.filter(
                    item => typeof item.value === 'number' && item.value > 0
                );
                return {
                    label: ds.label,
                    data: filtered,
                    parsing: ds.parsing,
                    backgroundColor: getBarColor(i),
                    hoverBackgroundColor: getBarColor(i, 'hover'),
                    borderWidth: 1,
                    borderRadius: 20
                };
            })
            .filter(dataset => dataset.data.length > 0);
    }, [data.datasets]);

    useEffect(() => {
        const ctx = canvasRef.current;
        if (!ctx) {
            return undefined;
        }

        if (data.scale === GraphScale.Logarithmic) {
            Chart.register(LogarithmicScale);
        }

        const htmlLegendPlugin = {
            id: 'htmlLegend',
            afterUpdate: chart => {
                const ul = legendRef.current;
                if (!ul) return;
                ul.innerHTML = '';
                const items = chart.options.plugins.legend.labels.generateLabels(chart);
                items.forEach(item => {
                    const li = document.createElement('li');
                    li.style.margin = tailwindConfig().theme.margin[1.5];

                    const button = document.createElement('button');
                    button.style.display = 'inline-flex';
                    button.style.alignItems = 'center';
                    button.style.opacity = item.hidden ? '.3' : '';
                    button.onclick = () => {
                        const idx = item.datasetIndex;
                        const visible = chart.isDatasetVisible(idx);
                        chart.setDatasetVisibility(idx, !visible);
                        chart.update();
                    };

                    const box = document.createElement('span');
                    box.style.display = 'block';
                    box.style.width = tailwindConfig().theme.width[3];
                    box.style.height = tailwindConfig().theme.height[3];
                    box.style.borderRadius = tailwindConfig().theme.borderRadius.full;
                    box.style.marginRight = tailwindConfig().theme.margin[1.5];
                    box.style.borderWidth = '3px';
                    box.style.borderColor = item.fillStyle;
                    box.style.pointerEvents = 'none';

                    const label = document.createElement('span');
                    label.className = 'text-slate-500 dark:text-slate-400';
                    label.style.fontSize = tailwindConfig().theme.fontSize.sm[0];
                    label.style.lineHeight = tailwindConfig().theme.fontSize.sm[1].lineHeight;
                    label.appendChild(document.createTextNode(item.text));

                    button.appendChild(box);
                    button.appendChild(label);
                    li.appendChild(button);
                    ul.appendChild(li);
                });
            }
        };

        chartRef.current = new Chart(ctx, {
            type: data.type,
            data: {
                datasets: processedDatasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {padding: 0},
                scales: {
                    x: {
                        type: 'category',
                        display: true,
                        border: {display: false},
                        grid: {display: true},
                        ticks: {display: true, padding: 10}
                    },
                    y: {
                        display: true,
                        type: data.scale,
                        border: {display: false},
                        grid: {display: true},
                        ticks: {display: true, padding: 10}
                    }
                },
                interaction: {
                    mode: 'nearest',
                    intersect: false
                },
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        enabled: true,
                        titleColor: darkMode
                            ? tooltipTitleColor.dark
                            : tooltipTitleColor.light,
                        bodyColor: darkMode
                            ? tooltipBodyColor.dark
                            : tooltipBodyColor.light,
                        backgroundColor: darkMode
                            ? tooltipBgColor.dark
                            : tooltipBgColor.light,
                        borderColor: darkMode
                            ? tooltipBorderColor.dark
                            : tooltipBorderColor.light,
                        callbacks: {
                            title: (ctx) => {
                                const ti = Array.isArray(ctx) ? ctx[0] : ctx;
                                return ti?.dataset.label || '';
                            },
                            label: (ctx) => {
                                const item = ctx?.dataset?.data?.[ctx.dataIndex];
                                if (!item || typeof item.value !== 'number') return '';
                                if (item.type === 'money' && item.currency) {
                                    return renderCurrencyValueRef.current({
                                        amount: item.value * 100,
                                        currency: item.currency
                                    });
                                }
                                if (item.type === 'percentage') {
                                    return `${item.value.toFixed(2)}%`;
                                }
                                return String(item.value);
                            }
                        }
                    }
                }
            },
            plugins: [htmlLegendPlugin]
        });

        return () => {
            chartRef.current?.destroy();
            chartRef.current = null;
            if (data.scale === GraphScale.Logarithmic) {
                Chart.unregister(LogarithmicScale);
            }
        };
    }, []);

    useEffect(() => {
        if (chartRef.current === null) {
            return;
        }

        chartRef.current.config.type = data.type;
        chartRef.current.data.datasets = processedDatasets;
        chartRef.current.options.scales.x.type = 'category';
        chartRef.current.options.scales.y.type = data.scale;
        chartRef.current.update();
    }, [data.scale, data.type, processedDatasets]);

    useEffect(() => {
        if (chartRef.current === null) {
            return;
        }

        chartRef.current.options.plugins.tooltip.titleColor = darkMode
            ? tooltipTitleColor.dark
            : tooltipTitleColor.light;
        chartRef.current.options.plugins.tooltip.bodyColor = darkMode
            ? tooltipBodyColor.dark
            : tooltipBodyColor.light;
        chartRef.current.options.plugins.tooltip.backgroundColor = darkMode
            ? tooltipBgColor.dark
            : tooltipBgColor.light;
        chartRef.current.options.plugins.tooltip.borderColor = darkMode
            ? tooltipBorderColor.dark
            : tooltipBorderColor.light;
        chartRef.current.update('none');
    }, [darkMode, tooltipBgColor.dark, tooltipBgColor.light, tooltipBodyColor.dark, tooltipBodyColor.light, tooltipBorderColor.dark, tooltipBorderColor.light, tooltipTitleColor.dark, tooltipTitleColor.light]);

    return (
        <>
            <canvas ref={canvasRef} className={className} style={style}/>
            <ul ref={legendRef} className="flex flex-wrap justify-center -m-1"/>
        </>
    );
}
